import React, { useState, useEffect } from 'react';
import { X, Save, Trash2, Plus, Edit3, GripVertical, BookOpen, Link, Target } from 'lucide-react';
import { api } from '../lib/api';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Competency {
  id: number;
  name: string;
  description: string;
  parent_name?: string;
  sort_order: number;
}

interface ContentField {
  id: number;
  name: string;
  description: string;
  content_type: string;
  difficulty_level: string;
  estimated_duration?: string;
  materials_needed?: string;
  learning_objectives?: string;
  assessment_criteria?: string;
  sort_order: number;
}

interface Mapping {
  id: number;
  competency_id: number;
  content_field_id: number;
  relationship_type: string;
  strength: number;
  description?: string;
  competency_name?: string;
  content_field_name?: string;
  sort_order: number;
}

interface UnifiedCurriculumEditorProps {
  curriculumId: number;
  curriculumName: string;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

type EditorTab = 'competencies' | 'content-fields' | 'mappings';

export const UnifiedCurriculumEditor: React.FC<UnifiedCurriculumEditorProps> = ({
  curriculumId,
  curriculumName,
  isOpen,
  onClose,
  onSave
}) => {
  const [activeTab, setActiveTab] = useState<EditorTab>('competencies');
  const [competencies, setCompetencies] = useState<Competency[]>([]);
  const [contentFields, setContentFields] = useState<ContentField[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<any>({});

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const contentTypes = [
    { value: 'practical', label: 'Praktisch' },
    { value: 'theoretical', label: 'Theoretisch' },
    { value: 'assessment', label: 'Bewertung' },
    { value: 'project', label: 'Projekt' },
    { value: 'experiment', label: 'Experiment' }
  ];

  const difficultyLevels = [
    { value: 'beginner', label: 'Anfänger' },
    { value: 'intermediate', label: 'Fortgeschritten' },
    { value: 'advanced', label: 'Experte' }
  ];

  const relationshipTypes = [
    { value: 'direct', label: 'Direkt' },
    { value: 'supporting', label: 'Unterstützend' },
    { value: 'prerequisite', label: 'Voraussetzung' },
    { value: 'assessment', label: 'Bewertung' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, curriculumId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [competenciesRes, contentFieldsRes, mappingsRes] = await Promise.all([
        api.get(`/competencies/curriculum/${curriculumId}`),
        api.get(`/content-fields/curriculum/${curriculumId}`),
        api.get(`/mappings/curriculum/${curriculumId}`)
      ]);

      if (competenciesRes.data.success) {
        setCompetencies(competenciesRes.data.data.sort((a: Competency, b: Competency) => a.sort_order - b.sort_order));
      }
      if (contentFieldsRes.data.success) {
        setContentFields(contentFieldsRes.data.data.sort((a: ContentField, b: ContentField) => a.sort_order - b.sort_order));
      }
      if (mappingsRes.data.success) {
        setMappings(mappingsRes.data.data.sort((a: Mapping, b: Mapping) => a.sort_order - b.sort_order));
      }
    } catch (error: any) {
      console.error('Fehler beim Laden der Daten:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;

    let newItems: any[] = [];
    let endpoint = '';

    switch (activeTab) {
      case 'competencies':
        const oldCompetencies = competencies;
        const newCompetencies = arrayMove(
          oldCompetencies,
          oldCompetencies.findIndex(item => item.id === active.id),
          oldCompetencies.findIndex(item => item.id === over.id)
        );
        setCompetencies(newCompetencies);
        newItems = newCompetencies;
        endpoint = '/competencies/reorder';
        break;
      case 'content-fields':
        const oldContentFields = contentFields;
        const newContentFields = arrayMove(
          oldContentFields,
          oldContentFields.findIndex(item => item.id === active.id),
          oldContentFields.findIndex(item => item.id === over.id)
        );
        setContentFields(newContentFields);
        newItems = newContentFields;
        endpoint = '/content-fields/reorder';
        break;
      case 'mappings':
        const oldMappings = mappings;
        const newMappings = arrayMove(
          oldMappings,
          oldMappings.findIndex(item => item.id === active.id),
          oldMappings.findIndex(item => item.id === over.id)
        );
        setMappings(newMappings);
        newItems = newMappings;
        endpoint = '/mappings/reorder';
        break;
    }

    // Sortierung im Backend aktualisieren
    try {
      await api.post(endpoint, {
        curriculum_id: curriculumId,
        items: newItems.map((item, index) => ({
          id: item.id,
          sort_order: index + 1
        }))
      });
    } catch (error: any) {
      console.error('Fehler beim Aktualisieren der Sortierung:', error);
      toast.error('Fehler beim Aktualisieren der Sortierung');
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.name?.trim()) {
        toast.error('Name ist erforderlich');
        return;
      }

      let endpoint = '';
      let data = { ...formData, curriculum_id: curriculumId };

      switch (activeTab) {
        case 'competencies':
          endpoint = editingItem?.id ? `/competencies/${editingItem.id}` : '/competencies';
          if (editingItem?.id) {
            await api.put(endpoint, data);
          } else {
            await api.post(endpoint, data);
          }
          break;
        case 'content-fields':
          endpoint = editingItem?.id ? `/content-fields/${editingItem.id}` : '/content-fields';
          if (editingItem?.id) {
            await api.put(endpoint, data);
          } else {
            await api.post(endpoint, data);
          }
          break;
        case 'mappings':
          endpoint = editingItem?.id ? `/mappings/${editingItem.id}` : '/mappings';
          if (editingItem?.id) {
            await api.put(endpoint, data);
          } else {
            await api.post(endpoint, data);
          }
          break;
      }

      toast.success('Erfolgreich gespeichert');
      setShowForm(false);
      setEditingItem(null);
      resetForm();
      loadData();
      onSave();
    } catch (error: any) {
      console.error('Fehler beim Speichern:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (item: any) => {
    setEditingItem(item);
    setFormData(item);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Möchten Sie dieses Element wirklich löschen?')) {
      return;
    }

    try {
      let endpoint = '';
      switch (activeTab) {
        case 'competencies':
          endpoint = `/competencies/${id}`;
          break;
        case 'content-fields':
          endpoint = `/content-fields/${id}`;
          break;
        case 'mappings':
          endpoint = `/mappings/${id}`;
          break;
      }

      await api.delete(endpoint);
      toast.success('Erfolgreich gelöscht');
      loadData();
      onSave();
    } catch (error: any) {
      console.error('Fehler beim Löschen:', error);
      toast.error(error.response?.data?.error || 'Fehler beim Löschen');
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    resetForm();
    setShowForm(true);
  };

  const resetForm = () => {
    switch (activeTab) {
      case 'competencies':
        setFormData({
          curriculum_id: curriculumId,
          name: '',
          description: '',
          parent_name: ''
        });
        break;
      case 'content-fields':
        setFormData({
          curriculum_id: curriculumId,
          name: '',
          description: '',
          content_type: 'practical',
          difficulty_level: 'beginner',
          estimated_duration: '',
          materials_needed: '',
          learning_objectives: '',
          assessment_criteria: ''
        });
        break;
      case 'mappings':
        setFormData({
          competency_id: 0,
          content_field_id: 0,
          relationship_type: 'direct',
          strength: 1.0,
          description: ''
        });
        break;
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingItem(null);
    resetForm();
  };

  const getCurrentItems = () => {
    switch (activeTab) {
      case 'competencies':
        return competencies;
      case 'content-fields':
        return contentFields;
      case 'mappings':
        return mappings;
      default:
        return [];
    }
  };

  const getTabIcon = (tab: EditorTab) => {
    switch (tab) {
      case 'competencies':
        return <Target className="w-4 h-4" />;
      case 'content-fields':
        return <BookOpen className="w-4 h-4" />;
      case 'mappings':
        return <Link className="w-4 h-4" />;
    }
  };

  const getTabLabel = (tab: EditorTab) => {
    switch (tab) {
      case 'competencies':
        return 'Kompetenzen';
      case 'content-fields':
        return 'Inhaltsfelder';
      case 'mappings':
        return 'Mappings';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Curriculum bearbeiten
            </h2>
            <p className="text-sm text-gray-600">{curriculumName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex h-[calc(90vh-80px)]">
          {/* Sidebar - Tabs und Liste */}
          <div className="w-1/3 border-r border-gray-200 bg-gray-50">
            {/* Tabs */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex space-x-1">
                {(['competencies', 'content-fields', 'mappings'] as EditorTab[]).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeTab === tab
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {getTabIcon(tab)}
                    <span>{getTabLabel(tab)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Add Button */}
            <div className="p-4 border-b border-gray-200">
              <button
                onClick={handleAddNew}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Neues {getTabLabel(activeTab).slice(0, -1)}</span>
              </button>
            </div>

            {/* Items List */}
            <div className="overflow-y-auto h-full">
              {loading ? (
                <div className="p-4 text-center text-gray-500">
                  Lade {getTabLabel(activeTab).toLowerCase()}...
                </div>
              ) : getCurrentItems().length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  Keine {getTabLabel(activeTab).toLowerCase()} vorhanden
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={getCurrentItems().map(item => item.id)} strategy={verticalListSortingStrategy}>
                    <div className="p-2">
                      {getCurrentItems().map((item) => (
                        <SortableItem
                          key={item.id}
                          item={item}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          activeTab={activeTab}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>

          {/* Main Content - Form */}
          <div className="flex-1 overflow-y-auto">
            {showForm ? (
              <div className="p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900">
                    {editingItem?.id ? `${getTabLabel(activeTab).slice(0, -1)} bearbeiten` : `Neues ${getTabLabel(activeTab).slice(0, -1)} erstellen`}
                  </h3>
                </div>

                <div className="space-y-6">
                  {activeTab === 'competencies' && (
                    <CompetencyForm formData={formData} setFormData={setFormData} />
                  )}
                  {activeTab === 'content-fields' && (
                    <ContentFieldForm formData={formData} setFormData={setFormData} contentTypes={contentTypes} difficultyLevels={difficultyLevels} />
                  )}
                  {activeTab === 'mappings' && (
                    <MappingForm formData={formData} setFormData={setFormData} relationshipTypes={relationshipTypes} competencies={competencies} contentFields={contentFields} />
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    onClick={handleSave}
                    className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    <Save className="w-4 h-4" />
                    <span>Speichern</span>
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <div className="text-center">
                  {getTabIcon(activeTab)}
                  <p className="mt-2">Wählen Sie ein {getTabLabel(activeTab).slice(0, -1).toLowerCase()} aus der Liste aus oder erstellen Sie ein neues</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Sortable Item Component
interface SortableItemProps {
  item: any;
  onEdit: (item: any) => void;
  onDelete: (id: number) => void;
  activeTab: EditorTab;
}

function SortableItem({ item, onEdit, onDelete, activeTab }: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 mb-2 rounded-md cursor-pointer transition-colors ${
        isDragging ? 'opacity-50' : 'bg-white border border-gray-200 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-2 flex-1">
          <div
            {...attributes}
            {...listeners}
            className="p-1 text-gray-400 hover:text-gray-600 cursor-grab"
          >
            <GripVertical className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-gray-900 text-sm">
              {item.name}
            </h4>
            <p className="text-xs text-gray-600 mt-1 line-clamp-2">
              {item.description}
            </p>
            {activeTab === 'mappings' && (
              <div className="flex items-center space-x-2 mt-2">
                <span className="text-xs text-blue-600">
                  {item.competency_name} ↔ {item.content_field_name}
                </span>
                <span className="text-xs text-gray-500">
                  {Math.round(item.strength * 100)}%
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-1 ml-2">
          <button
            onClick={() => onEdit(item)}
            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
          >
            <Edit3 className="w-3 h-3" />
          </button>
          <button
            onClick={() => onDelete(item.id)}
            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

// Form Components
interface CompetencyFormProps {
  formData: any;
  setFormData: (data: any) => void;
}

function CompetencyForm({ formData, setFormData }: CompetencyFormProps) {
  return (
    <>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name *
        </label>
        <input
          type="text"
          value={formData.name || ''}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Name der Kompetenz"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Beschreibung *
        </label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Beschreibung der Kompetenz"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Übergeordnete Kompetenz
        </label>
        <input
          type="text"
          value={formData.parent_name || ''}
          onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Name der übergeordneten Kompetenz (optional)"
        />
      </div>
    </>
  );
}

interface ContentFieldFormProps {
  formData: any;
  setFormData: (data: any) => void;
  contentTypes: any[];
  difficultyLevels: any[];
}

function ContentFieldForm({ formData, setFormData, contentTypes, difficultyLevels }: ContentFieldFormProps) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name *
          </label>
          <input
            type="text"
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Name des Inhaltsfelds"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Typ
          </label>
          <select
            value={formData.content_type || 'practical'}
            onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {contentTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Beschreibung *
        </label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Detaillierte Beschreibung der praktischen Aktivität"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Schwierigkeitsgrad
          </label>
          <select
            value={formData.difficulty_level || 'beginner'}
            onChange={(e) => setFormData({ ...formData, difficulty_level: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {difficultyLevels.map((level) => (
              <option key={level.value} value={level.value}>
                {level.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Geschätzte Dauer
          </label>
          <input
            type="text"
            value={formData.estimated_duration || ''}
            onChange={(e) => setFormData({ ...formData, estimated_duration: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="z.B. 45 Minuten"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Benötigte Materialien
        </label>
        <textarea
          value={formData.materials_needed || ''}
          onChange={(e) => setFormData({ ...formData, materials_needed: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Liste der benötigten Materialien"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Lernziele
        </label>
        <textarea
          value={formData.learning_objectives || ''}
          onChange={(e) => setFormData({ ...formData, learning_objectives: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Spezifische Lernziele"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Bewertungskriterien
        </label>
        <textarea
          value={formData.assessment_criteria || ''}
          onChange={(e) => setFormData({ ...formData, assessment_criteria: e.target.value })}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Kriterien für die Bewertung"
        />
      </div>
    </>
  );
}

interface MappingFormProps {
  formData: any;
  setFormData: (data: any) => void;
  relationshipTypes: any[];
  competencies: Competency[];
  contentFields: ContentField[];
}

function MappingForm({ formData, setFormData, relationshipTypes, competencies, contentFields }: MappingFormProps) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kompetenz *
          </label>
          <select
            value={formData.competency_id || 0}
            onChange={(e) => setFormData({ ...formData, competency_id: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={0}>Kompetenz auswählen</option>
            {competencies.map((comp) => (
              <option key={comp.id} value={comp.id}>
                {comp.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Inhaltsfeld *
          </label>
          <select
            value={formData.content_field_id || 0}
            onChange={(e) => setFormData({ ...formData, content_field_id: parseInt(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value={0}>Inhaltsfeld auswählen</option>
            {contentFields.map((field) => (
              <option key={field.id} value={field.id}>
                {field.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Beziehungstyp
          </label>
          <select
            value={formData.relationship_type || 'direct'}
            onChange={(e) => setFormData({ ...formData, relationship_type: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {relationshipTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Stärke (0.0 - 1.0)
          </label>
          <input
            type="number"
            min="0"
            max="1"
            step="0.1"
            value={formData.strength || 1.0}
            onChange={(e) => setFormData({ ...formData, strength: parseFloat(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="mt-1">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(formData.strength || 1.0) * 100}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-600 mt-1">
              {Math.round((formData.strength || 1.0) * 100)}% Stärke
            </p>
          </div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Beschreibung der Beziehung
        </label>
        <textarea
          value={formData.description || ''}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Beschreibung der Beziehung zwischen Kompetenz und Inhaltsfeld"
        />
      </div>
    </>
  );
}
